import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link as MuiLink,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config/environment';
import { FirmAccessSuspendedError } from '../utils/firmAccess';

// Background image from backend public folder (public/login.jpg)
const getLoginBackgroundImageUrl = () =>
  `${new URL(config.apiBaseUrl).origin}/images/login.jpg`;

// Google SVG icon
const GoogleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 256 262" style={{ marginRight: 8 }}>
    <path fill="#4285F4" d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027" />
    <path fill="#34A853" d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1" />
    <path fill="#FBBC05" d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782" />
    <path fill="#EB4335" d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251" />
  </svg>
);


const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [suspendedOpen, setSuspendedOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<string | null>(null);
  const [logoError, setLogoError] = useState(false);
  const { login, socialLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('access') === 'suspended') {
      setSuspendedOpen(true);
      return;
    }
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
    }
  }, [searchParams]);

  const logoUrl = `${config.apiBaseUrl}/system-settings/logo`;

  const handleSocialLogin = async (provider: string) => {
    setSocialLoading(provider);
    setError('');
    try {
      const redirectUrl = await socialLogin(provider);
      window.location.href = redirectUrl;
    } catch (err: any) {
      setError(`Failed to initialize ${provider} login. Please try again.`);
      setSocialLoading(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err: any) {
      if (err instanceof FirmAccessSuspendedError) {
        setSuspendedOpen(true);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Left panel - background image */}
      <Box
        sx={{
          flex: '0 0 40%',
          backgroundImage: `url(${getLoginBackgroundImageUrl()})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          display: { xs: 'none', md: 'block' },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.15) 0%, rgba(30,60,120,0.25) 100%)',
          },
        }}
      />

      {/* Right panel - login form */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4,
          backgroundColor: '#fff',
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 400,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Logo from system settings */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            {!logoError ? (
              <img
                src={logoUrl}
                alt="Logo"
                onError={() => setLogoError(true)}
                style={{
                  maxHeight: 120,
                  maxWidth: 330,
                  objectFit: 'contain',
                }}
              />
            ) : (
              <Typography variant="h5" component="div" color="text.primary" fontWeight={600}>
                PMP
              </Typography>
            )}
          </Box>

          <Typography component="h1" variant="h5" fontWeight={600} color="text.primary" gutterBottom>
            Sign into your account
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Dialog open={suspendedOpen} onClose={() => setSuspendedOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Your account is on hold</DialogTitle>
            <DialogContent>
              <DialogContentText>
                We&apos;re unable to sign you in right now. Please reach out to us at{' '}
                <MuiLink href="mailto:sohail@appelsolutions.com">sohail@appelsolutions.com</MuiLink>
                {' '}or{' '}
                <MuiLink href="mailto:PMP@appelsolutions.com">PMP@appelsolutions.com</MuiLink>
                {' '}and we will be glad to assist you.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button variant="contained" onClick={() => setSuspendedOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Social login buttons */}
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => handleSocialLogin('google')}
              disabled={!!socialLoading}
              startIcon={socialLoading === 'google' ? <CircularProgress size={18} /> : <GoogleIcon />}
              sx={{
                py: 1.25,
                borderColor: '#dadce0',
                color: '#3c4043',
                borderRadius: 2,
                fontWeight: 500,
                fontSize: '0.9rem',
                textTransform: 'none',
                '&:hover': { borderColor: '#bdc1c6', backgroundColor: '#f8f9fa' },
              }}
            >
              Sign in with Google
            </Button>
          </Box>

          <Divider sx={{ width: '100%', mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
              OR
            </Typography>
          </Divider>

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(0, 120, 200, 0.06)',
                  borderRadius: 2,
                },
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(0, 120, 200, 0.06)',
                  borderRadius: 2,
                },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                backgroundColor: '#1976d2',
                color: '#fff',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary" component="span">
                Don&apos;t have an account?{' '}
              </Typography>
              <Link to="/firm-registration" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" color="primary" component="span" fontWeight={500}>
                  Register here
                </Typography>
              </Link>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
